import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { forwardRef, NgModule, inject, provideAppInitializer } from '@angular/core';

import { AppComponent } from './app.component';
import { CommonModule, LocationStrategy } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgbDropdownConfig, NgbModal, NgbModalConfig, NgbModule, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';

import "@angular/compiler"
import { ExperienceFinderModule } from 'pg-lib/experience-finder/experience-finder.module';
import { PgChatModule } from 'pg-lib/pg-chat/pg-chat.module';
import { PgHelpModule } from 'pg-lib/pg-help/pg-help.module';
import { PgUiControlsModule } from 'pg-lib/pg-ui-controls/pg-ui-controls.module';
import { PgUiElementsModule } from 'pg-lib/pg-ui-elements/pg-ui-elements.module';
import { PgUiMoleculesModule } from 'pg-lib/pg-ui-molecules/pg-ui-molecules.module';
import { AnalyticsService } from 'pg-lib/services/analytics.service';
import { ApiInterceptor } from 'pg-lib/services/api-interceptor.service';
import { AuthService } from 'pg-lib/services/auth.service';
import { EntouranceDataService } from 'pg-lib/services/entourance-data.service';
import { EnvironmentService } from 'pg-lib/services/environment.service';
import { LocalizationService } from 'pg-lib/services/localization.service';
import { MapStylesService } from 'pg-lib/services/map-styles.service';
import { NotificationsService } from 'pg-lib/services/notifications.service';
import { OptionMapsService } from 'pg-lib/services/option-maps.service';
import { PersistenceService } from 'pg-lib/services/persistence.service';
import { PreserveQueryParamsPathLocationStrategy } from 'pg-lib/services/preserve-query-params.service';
import { TitleService } from 'pg-lib/services/title.service';
import { UpdateCheckerService } from 'pg-lib/services/update-checker.service';
import { AppRoutingModule } from './app-routing.module';
import { TotemWidgetTextComponent } from './widgets/totem-widget-text/totem-widget-text.component';
import { TotemWidgetCarouselComponent } from './widgets/totem-widget-carousel/totem-widget-carousel.component';
import { TotemWidgetProductsComponent } from './widgets/totem-widget-products/totem-widget-products.component';
import { TotemWidgetWeatherComponent } from './widgets/totem-widget-weather/totem-widget-weather.component';
import { TotemHomeComponent } from './routes/totem-home/totem-home.component';
import { TotemScreensaverComponent } from './elements/totem-screensaver/totem-screensaver.component';
import { TotemHeaderComponent } from './elements/totem-header/totem-header.component';
import { TotemCustomerCareBookComponent } from './routes/totem-customer-care-book/totem-customer-care-book.component';
import { TotemCustomerCareCallComponent } from './routes/totem-customer-care-call/totem-customer-care-call.component';
import { TotemWidgetImagelistComponent } from './widgets/totem-widget-imagelist/totem-widget-imagelist.component';
import { TotemBackgroundMapComponent } from './elements/totem-background-map/totem-background-map.component';
import { TotemWidgetIdentityComponent } from './widgets/totem-widget-identity/totem-widget-identity.component';
import { TotemHomeBottomComponent } from './elements/totem-home-bottom/totem-home-bottom.component';
import { TotemWidgetChecklistComponent } from './widgets/totem-widget-checklist/totem-widget-checklist.component';
import { TotemWidgetCountdownComponent } from './widgets/totem-widget-countdown/totem-widget-countdown.component';
import { TotemWidgetCustomerCareComponent } from './widgets/totem-widget-customer-care/totem-widget-customer-care.component';
import { TotemWidgetViaggiareSicuriComponent } from './widgets/totem-widget-viaggiare-sicuri/totem-widget-viaggiare-sicuri.component';
import { TotemWidgetHeroComponent } from './widgets/totem-widget-hero/totem-widget-hero.component';
import { TotemWidgetWelcomeComponent } from './widgets/totem-widget-welcome/totem-widget-welcome.component';
import { TotemWidgetClustersComponent } from './widgets/totem-widget-clusters/totem-widget-clusters.component';
import { TotemWidgetChallengeComponent } from './widgets/totem-widget-challenge/totem-widget-challenge.component';
import { TotemWidgetActivitiesComponent } from './widgets/totem-widget-activities/totem-widget-activities.component';
import { TotemWidgetButtonsComponent } from './widgets/totem-widget-buttons/totem-widget-buttons.component';
import { TotemWidgetDetailsComponent } from './widgets/totem-widget-details/totem-widget-details.component';
import { TotemWidgetDestinationComponent } from './widgets/totem-widget-destination/totem-widget-destination.component';
import { TotemWidgetDaySurveyComponent } from './widgets/totem-widget-day-survey/totem-widget-day-survey.component';
import { TotemFooterComponent } from './elements/totem-footer/totem-footer.component';
import { TotemProfileComponent } from './elements/totem-profile/totem-profile.component';
import { TotemProductComponent } from './elements/totem-product/totem-product.component';

@NgModule({ declarations: [
        AppComponent,
        TotemHomeComponent,
        TotemWidgetTextComponent,
        TotemWidgetCarouselComponent,
        TotemWidgetProductsComponent,
        TotemWidgetWeatherComponent,
        TotemScreensaverComponent,
        TotemHeaderComponent,
        TotemCustomerCareBookComponent,
        TotemCustomerCareCallComponent,
        TotemWidgetImagelistComponent,
        TotemBackgroundMapComponent,
        TotemWidgetIdentityComponent,
        TotemWidgetChecklistComponent,
        TotemWidgetCountdownComponent,
        TotemWidgetCustomerCareComponent,
        TotemWidgetViaggiareSicuriComponent,
        TotemWidgetHeroComponent,
        TotemWidgetWelcomeComponent,
        TotemWidgetClustersComponent,
        TotemWidgetChallengeComponent,
        TotemWidgetActivitiesComponent,
        TotemWidgetButtonsComponent,
        TotemWidgetDetailsComponent,
        TotemWidgetDestinationComponent,
        TotemWidgetDaySurveyComponent,
        TotemHomeBottomComponent,
        TotemFooterComponent,
        TotemProfileComponent,
        TotemProductComponent
    ],
    bootstrap: [AppComponent], imports: [CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        NgbModule,
        AppRoutingModule,
        PgUiElementsModule,
        PgUiControlsModule,
        PgUiMoleculesModule,
        PgChatModule,
        PgHelpModule,
        ExperienceFinderModule], providers: [provideAppInitializer(() => {
        const initializerFn = ((optionMapsService: OptionMapsService, environmentService: EnvironmentService, ngbDropdownConfig: NgbDropdownConfig, ngbTooltipConfig: NgbTooltipConfig, ngbModalConfig: NgbModalConfig, ngbModal: NgbModal, localizationService: LocalizationService, mapStylesService: MapStylesService, updateCheckerService: UpdateCheckerService, notificationsService: NotificationsService, persistenceService: PersistenceService, authService: AuthService, entouranceDataService: EntouranceDataService, analyticsService: AnalyticsService, titleService:TitleService): Function => {
                return async () => {
                    titleService.init();
                    persistenceService.activate({ requireTechnicalThirdParty: true });
                    ngbDropdownConfig.container = null;
                    ngbTooltipConfig.container = 'body';
                    ngbModalConfig.backdrop = 'static';
                    ngbModalConfig.keyboard = false;
                    window.addEventListener('popstate', (event) => {
                        if (ngbModal.hasOpenModals()) {
                            ngbModal.dismissAll();
                        }
                    });
                    await environmentService.init(environment, 'cluer-tour-operator');
                    if (environmentService.getTimeUntilManteinance() > 0) {
                        entouranceDataService.init(environmentService, authService);
                        await localizationService.init();
                        await optionMapsService.init();
                        await authService.init();
                        await mapStylesService.init();
                        await notificationsService.init();
                        updateCheckerService.init();
                        analyticsService.init('totem');
                    }
                };
            })(inject(OptionMapsService), inject(EnvironmentService), inject(NgbDropdownConfig), inject(NgbTooltipConfig), inject(NgbModalConfig), inject(NgbModal), inject(LocalizationService), inject(MapStylesService), inject(UpdateCheckerService), inject(NotificationsService), inject(PersistenceService), inject(AuthService), inject(EntouranceDataService), inject(AnalyticsService), inject(TitleService));
        return initializerFn();
      }), {
            provide: HTTP_INTERCEPTORS,
            useExisting: forwardRef(() => ApiInterceptor),
            multi: true
        }, {
            provide: LocationStrategy,
            useClass: PreserveQueryParamsPathLocationStrategy
        }, provideHttpClient(withInterceptorsFromDi())] 
    }) 
export class AppModule { }
